import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Button,
  ButtonGroup,
  Whisper,
  Icon,
  Dropdown,
  Popover
} from 'rsuite';
import { set, getOr, map, filter, isEmpty, isNull } from 'lodash/fp';
import { useDispatch, useSelector } from 'react-redux';
import DownloadPdf from '../../DownloadPdf';
import ReservationDetailsListPdf from '../ReservationDetailsListPdf';
import {
  getIsFetching,
  getOrdersByReservation
} from '../../../redux/Reservation/selectors/order-items';
import { getUserPermissionsAsMap } from '../../../redux/selectors/permission';
import { fetchReservationsOrderItems } from '../../../redux/Reservation/sagas/routines';
import { resetReservationsOrderItems } from '../../../redux/Reservation/actions/order-items';
import { getClientActiveColumns } from '../../../redux/selectors/client';
import { fetchClientColumns } from '../../../redux/sagas/client/routines';
import { hasViewAccess } from '../../../helpers/permission';
import { VIEW_ADDRESS } from '../../../constants/permissions/guest';

const PrintReservations = ({ label, fileName, data }) => {
  const dispatch = useDispatch();
  const printAll = useRef(true);
  const ordersByReservation = useSelector(getOrdersByReservation);
  const isFetchingOrders = useSelector(getIsFetching);
  const guestFields = useSelector(getClientActiveColumns);
  const permissions = useSelector(getUserPermissionsAsMap);

  const handlePrintAll = () => {
    printAll.current = true;
    dispatch(fetchReservationsOrderItems({ reservationId: map('id', data) }));
  };

  const handlePrintOrderOnly = () => {
    printAll.current = false;
    dispatch(fetchReservationsOrderItems({ reservationId: map('id', data) }));
  };

  const reservations = map(
    each => set('orders', getOr([], `${each.id}`, ordersByReservation), each),
    data
  );

  useEffect(
    () => {
      dispatch(fetchClientColumns());
      if (!isNull(ordersByReservation)) {
        setTimeout(() => {
          printAll.current = true;
          dispatch(resetReservationsOrderItems());
        }, 100);
      }
    },
    [ordersByReservation]
  );

  if (isEmpty(data)) return null;

  return isNull(ordersByReservation) ? (
    <ButtonGroup>
      <Button disabled={isFetchingOrders} onClick={handlePrintAll}>
        <Icon icon="print" /> {label}
      </Button>
      <Whisper
        placement="bottomEnd"
        trigger="click"
        speaker={({ left, top, className }, ref) => (
          <Popover ref={ref} className={className} style={{ left, top }} full>
            <Dropdown.Menu disabled={isFetchingOrders}>
              <Dropdown.Item onSelect={handlePrintAll}>All</Dropdown.Item>
              <Dropdown.Item onSelect={handlePrintOrderOnly}>
                Orders Only
              </Dropdown.Item>
            </Dropdown.Menu>
          </Popover>
        )}
      >
        <IconButton icon={<Icon icon="arrow-down" />} />
      </Whisper>
    </ButtonGroup>
  ) : (
    <DownloadPdf
      label={label}
      fileName={fileName}
      content={
        <ReservationDetailsListPdf
          data={
            printAll.current === true
              ? reservations
              : filter(each => !isEmpty(each.orders), reservations)
          }
          guestFields={guestFields}
          showLine1Address={hasViewAccess(VIEW_ADDRESS, permissions)}
        />
      }
    />
  );
};

PrintReservations.propTypes = {
  label: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  fileName: PropTypes.string.isRequired
};

PrintReservations.defaultProps = {
  label: 'Print'
};

export default PrintReservations;
